import type { ThreatLevelId } from "../core/MetaState.ts";
import type {
  LeaderboardClient,
  LeaderboardEntryDTO,
  RankedEntry,
  SubmitResult,
} from "./LeaderboardClient.ts";

export interface HttpLeaderboardClientOptions {
  baseUrl: string;
  timeoutMs?: number;
}

export class HttpLeaderboardClient implements LeaderboardClient {
  private readonly baseUrl: string;
  private readonly timeoutMs: number;

  constructor(options: HttpLeaderboardClientOptions) {
    this.baseUrl = options.baseUrl.replace(/\/$/, "");
    this.timeoutMs = options.timeoutMs ?? 2500;
  }

  async submitScore(
    entry: LeaderboardEntryDTO,
    signal?: AbortSignal,
  ): Promise<SubmitResult> {
    const submitInit: RequestInit = {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify(entry),
    };
    if (signal) submitInit.signal = signal;

    const response = await this.fetchWithTimeout("/api/submit", {
      ...submitInit,
    });

    if (!response.ok) {
      return this.mapSubmitFailure(response.status);
    }

    try {
      const payload = (await response.json()) as SubmitResult;
      return payload.ok ? payload : { ok: false, reason: "network" };
    } catch {
      return { ok: false, reason: "network" };
    }
  }

  async fetchTop(
    limit: number,
    selfId?: string,
    signal?: AbortSignal,
    threatLevel?: ThreatLevelId,
  ): Promise<RankedEntry[]> {
    const clamped = Math.max(1, Math.min(100, Math.trunc(limit)));
    const topInit: RequestInit = { method: "GET" };
    if (signal) topInit.signal = signal;
    // BL-95: pass our own id ONLY to get the per-row `self` flag back; the
    // server never returns any stable id. Omitted ⇒ a cacheable anon response.
    const selfQuery = selfId ? `&self=${encodeURIComponent(selfId)}` : "";
    // The population. Omitting it is legal and deterministic — the server
    // answers for `containment`, the same rule it applies to a submit without
    // a level, so no request can ever straddle two populations.
    const levelQuery = threatLevel
      ? `&threat=${encodeURIComponent(threatLevel)}`
      : "";
    const response = await this.fetchWithTimeout(
      `/api/leaderboard?limit=${clamped}${selfQuery}${levelQuery}`,
      topInit,
    );

    if (!response.ok) return [];
    try {
      const payload = (await response.json()) as RankedEntry[];
      return Array.isArray(payload) ? payload : [];
    } catch {
      return [];
    }
  }

  private async fetchWithTimeout(
    path: string,
    init: RequestInit,
  ): Promise<Response> {
    const controller = new AbortController();
    const timeout = window.setTimeout(() => controller.abort(), this.timeoutMs);

    const mergedSignal = this.mergeAbortSignals(init.signal, controller.signal);
    try {
      return await fetch(`${this.baseUrl}${path}`, {
        ...init,
        signal: mergedSignal,
      });
    } catch {
      return new Response(null, { status: 503 });
    } finally {
      clearTimeout(timeout);
    }
  }

  private mergeAbortSignals(
    externalSignal: AbortSignal | null | undefined,
    internalSignal: AbortSignal,
  ): AbortSignal {
    if (!externalSignal) return internalSignal;

    if (externalSignal.aborted) {
      const alreadyAborted = new AbortController();
      alreadyAborted.abort();
      return alreadyAborted.signal;
    }

    const merged = new AbortController();
    const abort = (): void => merged.abort();

    externalSignal.addEventListener("abort", abort, { once: true });
    internalSignal.addEventListener("abort", abort, { once: true });
    return merged.signal;
  }

  private mapSubmitFailure(status: number): SubmitResult {
    if (status === 400) return { ok: false, reason: "invalid" };
    if (status === 422) return { ok: false, reason: "blocked" };
    if (status === 429) return { ok: false, reason: "rate-limited" };
    return { ok: false, reason: "network" };
  }
}
